import React, { FC, ReactNode, useMemo } from 'react';
import { isNil } from 'rambda';
import styled, { css } from 'styled-components';
import { Heading, VerticalDivider, media } from '@higo/ui';
import { OverviewContainer } from './OverviewContainer';
import { OverviewDiseasesColumnWrapper } from './OverviewDiseasesColumnWrapper';
import { OverviewDiseasesColumnButtons } from './OverviewDiseasesColumnButtons';
import { Gender, HigoMode } from '@higo/api';
import {
  defaultToNA,
  formatDate,
  mapToOrDefaultNA,
} from '@higo/common/src/utils';
import { OverviewGeneralPatientInformation } from './OverviewGeneralPatientInformation';
import { OverviewImportantPatientCondition } from './OverviewImportantPatientCondition';
import { FormattedMessage } from 'react-intl';
import { PatientOverview } from '@higo/common/src/features/ExaminationPage/models';
import useMedia from 'react-use/lib/useMedia';
import { isMobileQuery } from '@higo/ui';
import Icon from '@ant-design/icons';
import { RedoOutlined } from '@ant-design/icons';

const StyledHeading = styled(Heading)(
  ({ theme }) => css`
    // margin-bottom: 3.9375rem;
    overflow: hidden;
    white-space: pre-wrap;
    text-overflow: ellipsis;

    ${media.desktop.min} {
      margin-bottom: 1rem;
    }

    ${media.mobile.max} {
      margin-bottom: 0.5rem;
      margin-top: 0;
    }
  `,
);

const GeneralInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 0.5rem;
`;

const HigoModeContainer = styled.div`
  display: inline;
  margin-left: auto;
  margin-right: 0;
`;

const HorizontalContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
`;

const UserName = styled.div`
  margin-left: 0.4rem;
  ${media.mobile.max} {
    margin-bottom: 0.8rem;
    margin-top: 0.3rem;
  }
`;

const NoNewExams = styled('p')(
  ({ theme }) => css`
    ${theme.typography.body};
    display: inline-block;
    margin-left: 0.5rem;
    // color: ${theme.palette.secondary['500']};
    color: black;
  `,
);

const NoNewExamsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
  margin-top: 0.5rem;
`;

export const GeneralInformationText = styled('span')(({ theme }) => css`
  ${theme.typography.body2};
  color: ${theme.palette.secondary['500']};

  font-variant-numeric: lining-nums tabular-nums;
  font-synthesis: none;

  & b,
  & strong {
    font-weight: 700;    
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    font-family: inherit;
  }
`);

export interface ExaminationPageOverviewProps {
  mode: HigoMode;
  patientOverview: PatientOverview;
  examinationId: number;
  patient: {
    id: number;
    idNumber?: string;
    firstName: string;
    lastName: string;
    gender?: Gender;
    dateOfBirth: Date;
  }; 
  generalSurveySlot?: ReactNode;
  patientExaminationsModalSlot?: ReactNode;
  isVisitOverview?: boolean;
  isVisitEnd?: boolean;
  webSocketData?: boolean;
}

export const ExaminationPageOverview: FC<ExaminationPageOverviewProps> = ({
  mode,
  patientOverview,
  patient,
  generalSurveySlot,
  patientExaminationsModalSlot,
  isVisitOverview,
  isVisitEnd,
  webSocketData,
}) => {
  const data = useMemo(
    () => ({
      fullName: `${patient.firstName} ${patient.lastName}`,
      idNumber:
        isNil(patient.idNumber) || !patient.idNumber
          ? '...'
          : defaultToNA(patient.idNumber),
      dob:
        typeof patient.dateOfBirth !== 'string'
          ? mapToOrDefaultNA<Date>((dob) => formatDate(dob, 'dd/MM/yyyy'))(
              patient.dateOfBirth,
            )
          : mapToOrDefaultNA<Date>((dob) => formatDate(dob, 'dd/MM/yyyy'))(
              new Date(patient.dateOfBirth),
            ),
    }),
    [patient],
  );

  const isHigoFamily = 'FAMILY' === mode;
  const isMobileView = useMedia(`${isMobileQuery}`);

  return (
    <OverviewContainer>
      <div id="overview-container">
        <UserName>
          <StyledHeading level={2}>
            {data.fullName}
            {/* <HigoModeContainer>
              <HigoModeLogo mode={mode}/>
            </HigoModeContainer> */}
          </StyledHeading>
        </UserName>
        <GeneralInfoContainer>
          <HorizontalContainer>
            <GeneralInformationText id="user-idnumber">
              ID:{' '}
              <b id="idnumber-value">{data.idNumber ? data.idNumber : '...'}</b>
            </GeneralInformationText>
            <GeneralInformationText id="birthdate">
              <FormattedMessage id="examinationPage.header.overview.birthday" />
              : <b id="birthdate-value">{data.dob}</b>
            </GeneralInformationText>
          </HorizontalContainer>
          {!isMobileView && patientExaminationsModalSlot}
        </GeneralInfoContainer>
        {isVisitOverview && !isVisitEnd && webSocketData && (
          <NoNewExamsContainer>
            <RedoOutlined
              size={20}
              spin
              style={{ fontSize: '16px', color: '#39C0FE' }}
            />
            <NoNewExams>Waiting for exams from the patient...</NoNewExams>
          </NoNewExamsContainer>
        )}
      </div>

      <VerticalDivider />

      {isHigoFamily && patientOverview && (
        <>
          <OverviewGeneralPatientInformation
            patientOverview={patientOverview}
          />

          <VerticalDivider />
        </>
      )}

      <OverviewDiseasesColumnWrapper>
        {isHigoFamily && !isMobileView && (
          <OverviewImportantPatientCondition
            patientOverview={patientOverview}
          />
        )}

        <OverviewDiseasesColumnButtons>
          {generalSurveySlot}
        </OverviewDiseasesColumnButtons>

      </OverviewDiseasesColumnWrapper>
    </OverviewContainer>
  );
};
